import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Upload, FileText, Sparkles, Edit2, Save, Trash2, Plus, 
  AlertCircle, CheckCircle, Loader2, ClipboardPaste, X, Brain, Briefcase,
  Square, CheckSquare, Settings2, RefreshCw, Calculator, Cloud, CloudOff, Clock, Target,
  Layers, Link2, Activity
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { PayoffDiagramButton } from './PayoffDiagramModal';
import PortfolioHealthAnalyzer from './PortfolioHealthAnalyzer';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Strategy types for grouping
const STRATEGY_TYPES = [
  { id: 'straddle', name: 'Straddle', legs: 2, description: 'Same strike call + put' },
  { id: 'strangle', name: 'Strangle', legs: 2, description: 'Different strike call + put' },
  { id: 'vertical_spread', name: 'Vertical Spread', legs: 2, description: 'Same type, different strikes' },
  { id: 'iron_condor', name: 'Iron Condor', legs: 4, description: 'Put spread + call spread' },
  { id: 'iron_butterfly', name: 'Iron Butterfly', legs: 4, description: 'ATM straddle + OTM wings' },
  { id: 'butterfly', name: 'Butterfly', legs: 3, description: 'Three strikes, same type' },
  { id: 'calendar_spread', name: 'Calendar Spread', legs: 2, description: 'Same strike, different expirations' },
  { id: 'diagonal_spread', name: 'Diagonal Spread', legs: 2, description: 'Different strike + expiration' },
  { id: 'custom', name: 'Custom Strategy', legs: 0, description: 'Any combination of legs' },
];

const PLATFORMS = [
  { id: 'tastytrade', name: 'Tastytrade', color: 'bg-red-100 text-red-700 border-red-200' },
  { id: 'schwab', name: 'Charles Schwab', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { id: 'fidelity', name: 'Fidelity', color: 'bg-green-100 text-green-700 border-green-200' },
  { id: 'td_ameritrade', name: 'TD Ameritrade', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  { id: 'etrade', name: 'E*TRADE', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  { id: 'robinhood', name: 'Robinhood', color: 'bg-lime-100 text-lime-700 border-lime-200' },
  { id: 'webull', name: 'Webull', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  { id: 'interactive_brokers', name: 'Interactive Brokers', color: 'bg-rose-100 text-rose-700 border-rose-200' },
  { id: 'thinkorswim', name: 'thinkorswim', color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
  { id: 'public', name: 'Public.com', color: 'bg-teal-100 text-teal-700 border-teal-200' },
  { id: 'other', name: 'Other', color: 'bg-gray-100 text-gray-700 border-gray-200' },
];

const PortfolioImport = ({ onImportComplete, onAnalyzePosition, onManagePosition }) => {
  // Auth context for auto-sync
  const { isAuthenticated, triggerAutoSync, syncStatus } = useAuth();
  
  const [rawInput, setRawInput] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parsedPositions, setParsedPositions] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [showImportArea, setShowImportArea] = useState(true);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [savedPositions, setSavedPositions] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState(new Set()); // Track selected position IDs for deletion
  const [editingPositionId, setEditingPositionId] = useState(null); // Track which saved position is being edited by ID
  const [editFormData, setEditFormData] = useState(null); // Temporary form data for editing
  const [recalculatingId, setRecalculatingId] = useState(null); // Track which position is being recalculated by ID
  const [recalculationResult, setRecalculationResult] = useState(null); // Store AI recalculation result
  const [showRecalcResult, setShowRecalcResult] = useState(false); // Show/hide recalculation result modal
  const [selectedPlatform, setSelectedPlatform] = useState(() => {
    // Load saved platform preference
    return localStorage.getItem('importedPlatformPreference') || 'tastytrade';
  });
  const [filterPlatform, setFilterPlatform] = useState('all');
  const textareaRef = React.useRef(null);
  const fileInputRef = React.useRef(null);
  
  // Grouping state
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [selectedForGroup, setSelectedForGroup] = useState(new Set());
  const [groupStrategyType, setGroupStrategyType] = useState('straddle');
  const [groupStrategyName, setGroupStrategyName] = useState('');
  const [showHealthAnalyzer, setShowHealthAnalyzer] = useState(false);

  // Generate unique ID for positions
  const generatePositionId = () => {
    return `pos_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // Ensure all positions have unique IDs
  const ensurePositionIds = (positions) => {
    return positions.map(pos => ({
      ...pos,
      _id: pos._id || generatePositionId()
    }));
  };

  // Load saved positions from localStorage on mount
  React.useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('importedPositions') || '[]');
      console.log('Loaded saved positions:', saved.length);
      // Ensure all positions have IDs
      const positionsWithIds = ensurePositionIds(saved);
      // Save back if we added IDs
      if (positionsWithIds.some((p, i) => p._id !== saved[i]?._id)) {
        localStorage.setItem('importedPositions', JSON.stringify(positionsWithIds));
      }
      setSavedPositions(positionsWithIds);
      if (positionsWithIds.length > 0) {
        setShowSaved(true);
      }
    } catch (error) {
      console.error('Error loading saved positions:', error);
      setSavedPositions([]);
    }
  }, []);

  // Save platform preference when it changes
  React.useEffect(() => {
    localStorage.setItem('importedPlatformPreference', selectedPlatform);
  }, [selectedPlatform]);

  // Get platform info by ID
  const getPlatform = (platformId) => {
    return PLATFORMS.find(p => p.id === platformId) || PLATFORMS[PLATFORMS.length - 1];
  };

  // Determine if position is bullish or bearish
  const isBullish = (position) => {
    const { option_type, quantity } = position;
    // Long Call or Short Put = Bullish
    if (option_type === 'call' && quantity > 0) return true;
    if (option_type === 'put' && quantity < 0) return true;
    // Long Put or Short Call = Bearish
    return false;
  };

  // Check if position expires within specified days
  const getExpirationStatus = (expirationDate) => {
    if (!expirationDate) return { expiresSoon: false, expiresInDays: null };
    
    const now = new Date();
    const expDate = new Date(expirationDate);
    const diffTime = expDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) {
      return { expiresSoon: true, expiresInDays: 0, status: 'EXPIRED' };
    } else if (diffDays === 1) {
      return { expiresSoon: true, expiresInDays: 1, status: 'EXPIRES TOMORROW' };
    } else if (diffDays <= 1) {
      return { expiresSoon: true, expiresInDays: diffDays, status: 'EXPIRES TODAY' };
    }
    return { expiresSoon: false, expiresInDays: diffDays };
  };

  // Play expiration warning sound
  const playExpirationSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Create a warning beep sequence
      const playBeep = (startTime, frequency) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(frequency, startTime);
        
        gainNode.gain.setValueAtTime(0.3, startTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + 0.3);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + 0.3);
      };
      
      // Play urgent warning pattern
      const now = audioContext.currentTime;
      playBeep(now, 880);        // High A
      playBeep(now + 0.35, 660); // E
      playBeep(now + 0.7, 880);  // High A
      
    } catch (e) {
      console.log('Audio not supported:', e);
    }
  };

  // Check for expiring positions on mount and show alerts
  useEffect(() => {
    const expiringPositions = savedPositions.filter(pos => {
      const status = getExpirationStatus(pos.expiration_date);
      return status.expiresSoon;
    });
    
    if (expiringPositions.length > 0) {
      // Play sound alert
      playExpirationSound();
      
      // Show toast notification
      const expiredCount = expiringPositions.filter(p => getExpirationStatus(p.expiration_date).expiresInDays === 0).length;
      const expiringSoonCount = expiringPositions.length - expiredCount;
      
      if (expiredCount > 0) {
        toast.error(`⚠️ ${expiredCount} position(s) have EXPIRED!`);
      }
      if (expiringSoonCount > 0) {
        toast.warning(`🔔 ${expiringSoonCount} position(s) expire within 1 day!`);
      }
    }
  }, [savedPositions]);

  // Calculate P/L percentage
  const calculatePnL = (position) => {
    const { entry_premium, current_premium, quantity } = position;
    
    if (!entry_premium || entry_premium === 0) {
      return { pnl: null, pnlPercent: null };
    }
    
    // If no current premium, assume break-even
    const currentPrice = current_premium || entry_premium;
    
    // For long positions (quantity > 0): profit when price goes up
    // For short positions (quantity < 0): profit when price goes down
    let pnlPercent;
    if (quantity > 0) {
      // Long position
      pnlPercent = ((currentPrice - entry_premium) / entry_premium) * 100;
    } else {
      // Short position
      pnlPercent = ((entry_premium - currentPrice) / entry_premium) * 100;
    }
    
    // Calculate dollar P/L (per contract = 100 shares)
    const contractMultiplier = 100;
    const pnlDollar = (currentPrice - entry_premium) * Math.abs(quantity) * contractMultiplier * (quantity > 0 ? 1 : -1);
    
    return { 
      pnl: pnlDollar, 
      pnlPercent: pnlPercent 
    };
  };

  // Filter positions by platform
  const filteredPositions = filterPlatform === 'all' 
    ? savedPositions 
    : savedPositions.filter(p => p.platform === filterPlatform);

  // Get unique platforms from saved positions
  const savedPlatforms = [...new Set(savedPositions.map(p => p.platform || 'other'))];

  // Handle analyze position
  const handleAnalyze = (position) => {
    console.log('PortfolioImport.handleAnalyze called with:', JSON.stringify(position, null, 2));
    if (onAnalyzePosition) {
      let formattedPosition;
      
      // Check if this is already a spread/grouped position with legs
      if (position.is_spread && position.legs && position.legs.length > 0) {
        // Handle spread/grouped position - keep existing legs structure
        formattedPosition = {
          ...position,
          underlying_symbol: position.underlying_symbol || position.symbol,
          is_option: true,
          is_spread: true,
          expiration_date: position.expiration_date,
          strategy_type: position.strategy_type,
          strategy_name: position.strategy_name,
          legs: position.legs.map(leg => ({
            ...leg,
            option_type: leg.option_type,
            strike_price: leg.strike_price,
            quantity: leg.quantity,
            average_open_price: leg.average_open_price || leg.entry_premium,
            close_price: leg.close_price || leg.current_premium,
            expiration_date: leg.expiration_date || position.expiration_date
          })),
          _actionTimestamp: Date.now()
        };
      } else {
        // Handle single position - create legs array
        formattedPosition = {
          ...position,
          underlying_symbol: position.underlying_symbol,
          is_option: true,
          is_spread: false,
          expiration_date: position.expiration_date,
          legs: [{
            option_type: position.option_type,
            strike_price: position.strike_price,
            quantity: position.quantity,
            average_open_price: position.entry_premium,
            close_price: position.current_premium,
            expiration_date: position.expiration_date
          }],
          _actionTimestamp: Date.now()
        };
      }
      
      console.log('PortfolioImport: Sending formattedPosition:', JSON.stringify(formattedPosition, null, 2));
      onAnalyzePosition(formattedPosition, 'analyze');
      toast.success(`Loaded ${position.underlying_symbol || position.symbol} for analysis`);
    } else {
      toast.error('Analysis not available. Please use the "My Position" tab to analyze.');
    }
  };

  // Toggle position selection for deletion (by ID)
  const togglePositionSelection = (positionId) => {
    setSelectedForDelete(prev => {
      const newSet = new Set(prev);
      if (newSet.has(positionId)) {
        newSet.delete(positionId);
      } else {
        newSet.add(positionId);
      }
      return newSet;
    });
  };

  // Select all visible positions
  const selectAllPositions = () => {
    const allIds = filteredPositions.map(pos => pos._id);
    setSelectedForDelete(new Set(allIds));
  };

  // Deselect all positions
  const deselectAllPositions = () => {
    setSelectedForDelete(new Set());
  };

  // Check if all filtered positions are selected
  const allSelected = filteredPositions.length > 0 && 
    filteredPositions.every(pos => selectedForDelete.has(pos._id));

  // Delete selected positions
  const deleteSelectedPositions = () => {
    if (selectedForDelete.size === 0) {
      toast.error('No positions selected');
      return;
    }
    
    const updated = savedPositions.filter(pos => !selectedForDelete.has(pos._id));
    localStorage.setItem('importedPositions', JSON.stringify(updated));
    setSavedPositions(updated);
    setSelectedForDelete(new Set());
    toast.success(`Deleted ${selectedForDelete.size} position(s)`);
    
    // Trigger auto-sync if authenticated
    if (isAuthenticated && triggerAutoSync) {
      triggerAutoSync();
    }
  };

  // Open group modal with selected positions
  const openGroupModal = () => {
    if (selectedForDelete.size < 2) {
      toast.error('Select at least 2 positions to group');
      return;
    }
    
    // Get selected positions
    const selected = savedPositions.filter(pos => selectedForDelete.has(pos._id));
    
    // Check if all positions have the same underlying
    const underlyings = [...new Set(selected.map(p => p.symbol?.toUpperCase()))];
    if (underlyings.length > 1) {
      toast.error('All positions must have the same underlying symbol');
      return;
    }
    
    // Auto-detect strategy type based on selected positions
    const detectedStrategy = detectStrategyType(selected);
    setGroupStrategyType(detectedStrategy);
    
    // Generate default name
    const symbol = underlyings[0] || 'Unknown';
    const strategyInfo = STRATEGY_TYPES.find(s => s.id === detectedStrategy);
    setGroupStrategyName(`${symbol} ${strategyInfo?.name || 'Strategy'}`);
    
    setSelectedForGroup(new Set(selectedForDelete));
    setShowGroupModal(true);
  };

  // Auto-detect strategy type from positions
  const detectStrategyType = (positions) => {
    if (positions.length < 2) return 'custom';
    
    const calls = positions.filter(p => p.option_type?.toLowerCase() === 'call');
    const puts = positions.filter(p => p.option_type?.toLowerCase() === 'put');
    const strikes = [...new Set(positions.map(p => p.strike_price))];
    const expirations = [...new Set(positions.map(p => p.expiration_date?.substring(0, 10)))];
    
    // Check for straddle (1 call + 1 put, same strike)
    if (calls.length === 1 && puts.length === 1 && strikes.length === 1) {
      return 'straddle';
    }
    
    // Check for strangle (1 call + 1 put, different strikes)
    if (calls.length === 1 && puts.length === 1 && strikes.length === 2) {
      return 'strangle';
    }
    
    // Check for vertical spread (2 same type, different strikes, same expiration)
    if ((calls.length === 2 || puts.length === 2) && strikes.length === 2 && expirations.length === 1) {
      return 'vertical_spread';
    }
    
    // Check for iron condor (2 calls + 2 puts)
    if (calls.length === 2 && puts.length === 2) {
      return 'iron_condor';
    }
    
    // Check for butterfly (3 legs)
    if (positions.length === 3 && (calls.length === 3 || puts.length === 3)) {
      return 'butterfly';
    }
    
    // Check for calendar spread (same strike, different expirations)
    if (positions.length === 2 && strikes.length === 1 && expirations.length === 2) {
      return 'calendar_spread';
    }
    
    // Check for diagonal spread (different strike, different expiration)
    if (positions.length === 2 && strikes.length === 2 && expirations.length === 2) {
      return 'diagonal_spread';
    }
    
    return 'custom';
  };

  // Group selected positions into a spread
  const groupPositions = () => {
    const selected = savedPositions.filter(pos => selectedForGroup.has(pos._id));
    
    if (selected.length < 2) {
      toast.error('Need at least 2 positions to group');
      return;
    }
    
    // Calculate combined values
    const totalCost = selected.reduce((sum, p) => sum + (p.entry_premium || 0) * (p.quantity || 1) * 100, 0);
    const totalValue = selected.reduce((sum, p) => sum + (p.current_premium || p.entry_premium || 0) * (p.quantity || 1) * 100, 0);
    const totalPnL = selected.reduce((sum, p) => {
      const entry = (p.entry_premium || 0) * (p.quantity || 1) * 100;
      const current = (p.current_premium || p.entry_premium || 0) * (p.quantity || 1) * 100;
      return sum + (current - entry);
    }, 0);
    
    // Get the earliest import timestamp
    const importTimestamp = selected.reduce((earliest, p) => {
      const ts = p.import_timestamp || p.imported_at;
      if (!ts) return earliest;
      if (!earliest) return ts;
      return new Date(ts) < new Date(earliest) ? ts : earliest;
    }, null);
    
    // Create the grouped spread
    const groupedSpread = {
      _id: generatePositionId(),
      is_spread: true,
      strategy_type: groupStrategyType,
      strategy_name: groupStrategyName || `${selected[0].symbol} ${STRATEGY_TYPES.find(s => s.id === groupStrategyType)?.name || 'Strategy'}`,
      symbol: selected[0].symbol?.toUpperCase(),
      underlying_symbol: selected[0].symbol?.toUpperCase(),
      expiration_date: selected.map(p => p.expiration_date).sort()[0], // Use earliest expiration
      legs: selected.map(leg => ({
        ...leg,
        _id: leg._id, // Keep original IDs for reference
      })),
      total_quantity: Math.min(...selected.map(p => Math.abs(p.quantity || 1))),
      total_cost_basis: Math.round(totalCost * 100) / 100,
      total_market_value: Math.round(totalValue * 100) / 100,
      total_unrealized_pnl: Math.round(totalPnL * 100) / 100,
      platform: selected[0].platform || 'other',
      import_timestamp: importTimestamp || new Date().toISOString(),
      grouped_at: new Date().toISOString(),
      description: buildStrategyDescription(selected, groupStrategyType),
    };
    
    // Remove individual positions and add the grouped spread
    const remainingPositions = savedPositions.filter(pos => !selectedForGroup.has(pos._id));
    const updated = [...remainingPositions, groupedSpread];
    
    localStorage.setItem('importedPositions', JSON.stringify(updated));
    setSavedPositions(updated);
    setSelectedForDelete(new Set());
    setSelectedForGroup(new Set());
    setShowGroupModal(false);
    
    toast.success(`Grouped ${selected.length} positions into "${groupedSpread.strategy_name}"`);
    
    // Trigger auto-sync if authenticated
    if (isAuthenticated && triggerAutoSync) {
      triggerAutoSync();
    }
  };

  // Build strategy description from legs
  const buildStrategyDescription = (legs, strategyType) => {
    const symbol = legs[0]?.symbol?.toUpperCase() || '';
    const strikes = legs.map(l => l.strike_price).sort((a, b) => a - b);
    const types = legs.map(l => `${l.quantity > 0 ? '+' : ''}${l.quantity} ${l.option_type?.charAt(0).toUpperCase()}@${l.strike_price}`);
    
    switch (strategyType) {
      case 'straddle':
        return `${symbol} $${strikes[0]} Straddle`;
      case 'strangle':
        return `${symbol} $${Math.min(...strikes)}P/$${Math.max(...strikes)}C Strangle`;
      case 'vertical_spread':
        return `${symbol} $${strikes[0]}/$${strikes[1]} Spread`;
      case 'iron_condor':
        return `${symbol} $${strikes[0]}/${strikes[1]}P - $${strikes[2]}/${strikes[3]}C IC`;
      case 'butterfly':
        return `${symbol} $${strikes[0]}/${strikes[1]}/${strikes[2]} Butterfly`;
      case 'calendar_spread':
        return `${symbol} $${strikes[0]} Calendar`;
      case 'diagonal_spread':
        return `${symbol} $${strikes[0]}/$${strikes[1]} Diagonal`;
      default:
        return types.join(', ');
    }
  };

  // Ungroup a spread back into individual legs
  const ungroupSpread = (spreadId) => {
    const spread = savedPositions.find(p => p._id === spreadId);
    if (!spread || !spread.is_spread || !spread.legs) {
      toast.error('Cannot ungroup this position');
      return;
    }
    
    // Restore individual legs with new IDs
    const restoredLegs = spread.legs.map(leg => ({
      ...leg,
      _id: generatePositionId(), // Generate new IDs for restored legs
      ungrouped_from: spread.strategy_name,
      ungrouped_at: new Date().toISOString(),
    }));
    
    // Remove the spread and add back individual legs
    const remainingPositions = savedPositions.filter(pos => pos._id !== spreadId);
    const updated = [...remainingPositions, ...restoredLegs];
    
    localStorage.setItem('importedPositions', JSON.stringify(updated));
    setSavedPositions(updated);
    
    toast.success(`Ungrouped "${spread.strategy_name}" into ${restoredLegs.length} individual positions`);
    
    // Trigger auto-sync if authenticated
    if (isAuthenticated && triggerAutoSync) {
      triggerAutoSync();
    }
  };

  // Delete saved position (single, by ID)
  const deleteSavedPosition = (positionId) => {
    const updated = savedPositions.filter(pos => pos._id !== positionId);
    localStorage.setItem('importedPositions', JSON.stringify(updated));
    setSavedPositions(updated);
    setSelectedForDelete(prev => {
      const newSet = new Set(prev);
      newSet.delete(positionId);
      return newSet;
    });
    // Close edit form if we're deleting the position being edited
    if (editingPositionId === positionId) {
      setEditingPositionId(null);
      setEditFormData(null);
    }
    toast.success('Position deleted');
    
    // Trigger auto-sync if authenticated
    if (isAuthenticated && triggerAutoSync) {
      triggerAutoSync();
    }
  };

  // Start editing a saved position (by ID)
  const startEditingSaved = (positionId) => {
    const position = savedPositions.find(p => p._id === positionId);
    if (position) {
      setEditingPositionId(positionId);
      setEditFormData({ ...position });
    }
  };

  // Cancel editing
  const cancelEditingSaved = () => {
    setEditingPositionId(null);
    setEditFormData(null);
  };

  // Update edit form field
  const updateEditFormField = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Save edited position (by ID)
  const saveEditedPosition = () => {
    console.log('Save called - editingPositionId:', editingPositionId, 'editFormData:', editFormData);
    
    if (!editingPositionId || !editFormData) {
      console.log('Save aborted - invalid ID or no form data');
      toast.error('Error: Unable to save. Please try again.');
      return;
    }
    
    // Validate required fields
    if (!editFormData.underlying_symbol || !editFormData.strike_price) {
      toast.error('Symbol and Strike price are required');
      return;
    }
    
    // Find the position index by ID
    const positionIndex = savedPositions.findIndex(p => p._id === editingPositionId);
    if (positionIndex === -1) {
      console.log('Save aborted - position not found');
      toast.error('Error: Position not found. Please try again.');
      return;
    }
    
    const updated = [...savedPositions];
    updated[positionIndex] = {
      ...savedPositions[positionIndex], // Keep original fields including _id
      ...editFormData, // Override with edited fields
      edited_at: new Date().toISOString(),
      needs_recalculation: true
    };
    
    console.log('Saving updated positions:', updated);
    
    localStorage.setItem('importedPositions', JSON.stringify(updated));
    setSavedPositions(updated);
    setEditingPositionId(null);
    setEditFormData(null);
    toast.success('Position updated! Click "Recalculate" for fresh AI analysis.');
    
    // Trigger auto-sync if authenticated
    if (isAuthenticated && triggerAutoSync) {
      triggerAutoSync();
    }
  };

  // Recalculate position with AI (by ID)
  const recalculatePosition = async (position) => {
    setRecalculatingId(position._id);
    
    try {
      // Get current stock price for more accurate analysis
      const priceResponse = await axios.get(`${API}/stocks/${position.underlying_symbol}/price`);
      const currentPrice = priceResponse.data?.price || priceResponse.data?.currentPrice;
      
      // Call AI management endpoint for comprehensive analysis
      const response = await axios.post(`${API}/positions/manage`, {
        position: {
          ...position,
          current_stock_price: currentPrice,
          is_option: true
        }
      });
      
      // Store result and show modal
      setRecalculationResult({
        position,
        analysis: response.data,
        currentPrice,
        timestamp: new Date().toISOString()
      });
      setShowRecalcResult(true);
      
      // Mark position as recalculated (find by ID)
      const positionIndex = savedPositions.findIndex(p => p._id === position._id);
      if (positionIndex !== -1) {
        const updated = [...savedPositions];
        updated[positionIndex] = {
          ...position,
          needs_recalculation: false,
          last_recalculated: new Date().toISOString()
        };
        localStorage.setItem('importedPositions', JSON.stringify(updated));
        setSavedPositions(updated);
      }
      
      toast.success('AI recalculation complete!');
    } catch (error) {
      console.error('Recalculation error:', error);
      toast.error('Failed to recalculate. Please try again.');
    } finally {
      setRecalculatingId(null);
    }
  };

  // Close recalculation result modal
  const closeRecalcResult = () => {
    setShowRecalcResult(false);
    setRecalculationResult(null);
  };

  // Clear all saved positions
  const clearAllSaved = () => {
    localStorage.removeItem('importedPositions');
    setSavedPositions([]);
    setSelectedForDelete(new Set());
    toast.success('All saved positions cleared');
    
    // Trigger auto-sync if authenticated
    if (isAuthenticated && triggerAutoSync) {
      triggerAutoSync();
    }
  };

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/tiff', 'image/webp', 'text/plain', 'text/csv'];
    if (!validTypes.includes(file.type) && !file.name.endsWith('.csv') && !file.name.endsWith('.txt')) {
      toast.error('Please upload an image (PNG, JPG, TIFF) or text file (CSV, TXT)');
      return;
    }

    setUploadedFile(file);
    setUploadProgress(10);
    toast.info(`Uploading ${file.name}...`);

    try {
      // For text/CSV files: try the structured broker-CSV parser first (Robinhood),
      // then fall back to loading the raw text for AI parsing.
      if (file.type === 'text/plain' || file.type === 'text/csv' || file.name.endsWith('.csv') || file.name.endsWith('.txt')) {
        const text = await file.text();
        setUploadProgress(60);
        try {
          const csvResp = await axios.post(`${API}/portfolio/parse-csv`, { csv_text: text });
          if (csvResp.data?.success && csvResp.data.positions?.length > 0) {
            setParsedPositions(csvResp.data.positions);
            setShowImportArea(false);
            if (csvResp.data.broker) setSelectedPlatform(csvResp.data.broker);
            const s = csvResp.data.summary || {};
            setUploadProgress(100);
            const warn = (csvResp.data.warnings || []).length ? ` (${csvResp.data.warnings.length} warning(s))` : '';
            toast.success(`Detected ${csvResp.data.broker} CSV: ${s.total} position(s) — ${s.stocks || 0} stock, ${s.options || 0} option${warn}. Review & Save to confirm.`);
            return;
          }
        } catch (csvErr) {
          // 422 = not a recognized broker CSV → fall back to AI parse of the raw text
          if (csvErr.response?.status && csvErr.response.status !== 422) {
            console.warn('CSV parse error, falling back to text:', csvErr.response?.status);
          }
        }
        // Fallback: load raw text for AI parsing
        setRawInput(text);
        setUploadProgress(100);
        toast.success(`Loaded ${file.name} - Click "Parse with AI" to continue`);
        return;
      }

      // For images, upload to backend for OCR
      setUploadProgress(30);
      const formData = new FormData();
      formData.append('file', file);

      toast.loading('Processing image with AI...', { id: 'image-processing' });
      
      const response = await axios.post(`${API}/portfolio/parse-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000, // 90 second timeout for image processing
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 50) / progressEvent.total) + 30;
          setUploadProgress(progress);
        }
      });

      toast.dismiss('image-processing');
      setUploadProgress(90);
      console.log('Image parse response:', response.data);

      if (response.data.success) {
        if (response.data.positions && response.data.positions.length > 0) {
          // Positions were extracted directly
          setParsedPositions(response.data.positions);
          setShowImportArea(false);
          setUploadProgress(100);
          toast.success(`Found ${response.data.positions.length} positions in image!`);
        } else if (response.data.extracted_text) {
          // Text was extracted but needs parsing
          setRawInput(response.data.extracted_text);
          setUploadProgress(100);
          toast.success('Image processed! Review the extracted text and click "Parse with AI".');
        } else {
          setUploadProgress(100);
          toast.warning('Could not find positions in image. Try a clearer screenshot or paste text manually.');
        }
      } else {
        toast.dismiss('image-processing');
        toast.error(response.data.error || 'Failed to process image');
        setUploadProgress(0);
      }
    } catch (error) {
      toast.dismiss('image-processing');
      console.error('Upload error:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.error || error.message || 'Failed to upload file';
      toast.error(`Error: ${errorMsg}`);
      setUploadProgress(0);
    }
  };

  // Parse pasted text using AI
  const parseWithAI = async () => {
    // Get value from controlled state
    const inputValue = rawInput || '';
    
    if (!inputValue.trim()) {
      toast.error('Please paste or upload your portfolio data first');
      return;
    }

    setParsing(true);
    try {
      const response = await axios.post(`${API}/portfolio/parse`, {
        raw_text: inputValue,
        current_date: new Date().toISOString().split('T')[0] // Pass current date to AI
      });

      if (response.data.success && response.data.positions) {
        setParsedPositions(response.data.positions);
        setShowImportArea(false);
        toast.success(`Parsed ${response.data.positions.length} positions! Please review and edit if needed.`);
      } else {
        toast.error(response.data.error || 'Failed to parse portfolio data');
      }
    } catch (error) {
      console.error('Parse error:', error);
      toast.error(error.response?.data?.detail || 'Failed to parse portfolio data');
    } finally {
      setParsing(false);
    }
  };

  // Update a position field
  const updatePosition = (index, field, value) => {
    const updated = [...parsedPositions];
    updated[index] = { ...updated[index], [field]: value };
    setParsedPositions(updated);
  };

  // Delete a position
  const deletePosition = (index) => {
    const updated = parsedPositions.filter((_, i) => i !== index);
    setParsedPositions(updated);
    toast.success('Position removed');
  };

  // Add a new empty position
  const addPosition = () => {
    setParsedPositions([
      ...parsedPositions,
      {
        symbol: '',
        underlying_symbol: '',
        option_type: 'call',
        strike_price: '',
        expiration_date: '',
        quantity: 1,
        entry_premium: '',
        current_premium: '',
        is_option: true
      }
    ]);
    setEditingIndex(parsedPositions.length);
  };

  // Save imported positions
  const savePositions = () => {
    // Validate positions
    const validPositions = parsedPositions.filter(p => 
      p.underlying_symbol && (p.strike_price || !p.is_option)
    );

    if (validPositions.length === 0) {
      toast.error('No valid positions to save');
      return;
    }

    try {
      // Store in localStorage with platform info and unique IDs
      const existingImported = JSON.parse(localStorage.getItem('importedPositions') || '[]');
      const platformName = getPlatform(selectedPlatform).name;
      const newImported = [...ensurePositionIds(existingImported), ...validPositions.map(p => ({
        ...p,
        _id: generatePositionId(), // Add unique ID
        imported_at: new Date().toISOString(),
        platform: selectedPlatform,
        source: 'manual_import'
      }))];
      
      localStorage.setItem('importedPositions', JSON.stringify(newImported));
      console.log('Saved positions to localStorage:', newImported.length, 'total');

      // Update saved positions state
      setSavedPositions(newImported);
      setShowSaved(true);

      toast.success(`Saved ${validPositions.length} positions to ${platformName}! Data will persist after refresh.`);
      
      // Trigger auto-sync if authenticated
      if (isAuthenticated && triggerAutoSync) {
        triggerAutoSync();
      }
      
      if (onImportComplete) {
        onImportComplete(validPositions);
      }

      // Reset import area
      setParsedPositions([]);
      setRawInput('');
      setShowImportArea(true);
    } catch (error) {
      console.error('Error saving positions:', error);
      toast.error('Failed to save positions. Please try again.');
    }
  };

  // Clear all
  const clearAll = () => {
    setParsedPositions([]);
    setRawInput('');
    setUploadedFile(null);
    setUploadProgress(0);
    setShowImportArea(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Card className="glass-effect border-0 shadow-lg">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg">
              <Upload className="h-6 w-6 text-white" />
            </div>
            <div>
              <CardTitle>Import Portfolio</CardTitle>
              <CardDescription>
                Upload a screenshot or paste your positions - AI will parse them
              </CardDescription>
            </div>
          </div>
          
          {/* Cloud Sync Status Indicator */}
          {isAuthenticated && (
            <div className="flex items-center space-x-2">
              {syncStatus === 'syncing' && (
                <div className="flex items-center space-x-1 text-blue-500 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Syncing...</span>
                </div>
              )}
              {syncStatus === 'synced' && (
                <div className="flex items-center space-x-1 text-green-500 text-sm">
                  <Cloud className="h-4 w-4" />
                  <span>Saved to cloud</span>
                </div>
              )}
              {syncStatus === 'error' && (
                <div className="flex items-center space-x-1 text-red-500 text-sm">
                  <CloudOff className="h-4 w-4" />
                  <span>Sync failed</span>
                </div>
              )}
              {syncStatus === 'idle' && (
                <div className="flex items-center space-x-1 text-gray-400 text-sm" title="Auto-sync enabled">
                  <Cloud className="h-4 w-4" />
                </div>
              )}
            </div>
          )}
          {parsedPositions.length > 0 && (
            <Badge variant="outline" className="text-lg px-3 py-1">
              {parsedPositions.length} positions
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {showImportArea ? (
          <>
            {/* Platform Selector */}
            <div className="space-y-2">
              <Label className="flex items-center space-x-2">
                <Briefcase className="h-4 w-4" />
                <span>Select Platform / Broker</span>
              </Label>
              <Select value={selectedPlatform} onValueChange={setSelectedPlatform}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select platform" />
                </SelectTrigger>
                <SelectContent>
                  {PLATFORMS.map(platform => (
                    <SelectItem key={platform.id} value={platform.id}>
                      <div className="flex items-center">
                        <span>{platform.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* File Upload Area */}
            <div className="space-y-3">
              <Label className="flex items-center space-x-2">
                <Upload className="h-4 w-4" />
                <span>Upload Screenshot or File</span>
              </Label>
              
              {/* Drop Zone */}
              <div 
                className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-indigo-400 hover:bg-indigo-50 transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-indigo-500', 'bg-indigo-50'); }}
                onDragLeave={(e) => { e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-50'); }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('border-indigo-500', 'bg-indigo-50');
                  const file = e.dataTransfer.files[0];
                  if (file) {
                    const event = { target: { files: [file] } };
                    handleFileUpload(event);
                  }
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/tiff,image/webp,.csv,.txt"
                  onChange={handleFileUpload}
                  className="hidden"
                  data-testid="file-upload-input"
                />
                <Upload className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                <p className="text-sm font-medium text-gray-700">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  PNG, JPG, TIFF, CSV, or TXT files
                </p>
                {uploadedFile && (
                  <div className="mt-2 flex items-center justify-center space-x-2 text-sm text-indigo-600">
                    <CheckCircle className="h-4 w-4" />
                    <span>{uploadedFile.name}</span>
                  </div>
                )}
                {uploadProgress > 0 && uploadProgress < 100 && (
                  <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-indigo-600 h-2 rounded-full transition-all" 
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-gray-500">Or type/paste manually</span>
              </div>
            </div>

            {/* Text Input Area */}
            <div className="space-y-2">
              <Label className="flex items-center space-x-2">
                <FileText className="h-4 w-4" />
                <span>Paste your portfolio data</span>
              </Label>
              <div className="relative">
                <textarea
                  ref={textareaRef}
                  value={rawInput}
                  onChange={(e) => {
                    setRawInput(e.target.value);
                  }}
                  onPaste={(e) => {
                    // Support screenshot/image paste (Ctrl+V of an image)
                    const items = e.clipboardData?.items || [];
                    for (const item of items) {
                      if (item.type && item.type.startsWith('image/')) {
                        const file = item.getAsFile();
                        if (file) {
                          e.preventDefault();
                          toast.info('Image detected in clipboard — processing screenshot...');
                          handleFileUpload({ target: { files: [file] } });
                          return;
                        }
                      }
                    }
                    // Otherwise let native text paste flow into the controlled input
                  }}
                  placeholder={`Paste your positions here. Examples:

AAPL 230C 02/21/2026 Long 5 @ $3.50
MSFT 400P Mar 2026 Short 2 @ $8.25
Symbol: TSLA, Strike: 250, Put, Exp: 3/15/26`}
                  className="w-full min-h-[200px] p-4 font-mono text-sm border-2 rounded-lg border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 bg-white resize-y"
                  data-testid="portfolio-import-textarea"
                  autoComplete="off"
                  spellCheck="false"
                />
                {/* Paste button as alternative */}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="absolute top-2 right-2 text-xs z-10"
                  data-testid="paste-btn"
                  onClick={async () => {
                    // Try the async Clipboard API — supports both images (screenshots) and text
                    try {
                      if (navigator.clipboard && navigator.clipboard.read) {
                        const clipboardItems = await navigator.clipboard.read();
                        for (const clipItem of clipboardItems) {
                          const imageType = clipItem.types.find(t => t.startsWith('image/'));
                          if (imageType) {
                            const blob = await clipItem.getType(imageType);
                            const ext = imageType.split('/')[1] || 'png';
                            const file = new File([blob], `screenshot.${ext}`, { type: imageType });
                            toast.info('Screenshot found in clipboard — processing...');
                            handleFileUpload({ target: { files: [file] } });
                            return;
                          }
                        }
                      }
                      // No image found — read text
                      const text = await navigator.clipboard.readText();
                      if (text && text.trim()) {
                        setRawInput(text);
                        toast.success(`Pasted ${text.length} characters! Click "Parse with AI" to continue.`);
                      } else {
                        toast.error('Clipboard is empty. Copy your portfolio text or a screenshot first, then click Paste.');
                      }
                    } catch (err) {
                      console.error('Clipboard error:', err);
                      toast.error('Clipboard access was blocked by your browser. Click inside the box and press Ctrl+V (Cmd+V on Mac) to paste text or a screenshot directly.');
                      if (textareaRef.current) textareaRef.current.focus();
                    }
                  }}
                >
                  <ClipboardPaste className="h-3 w-3 mr-1" />
                  Paste
                </Button>
              </div>
              
              {/* Show pasted content info */}
              {rawInput && rawInput.trim() && (
                <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700">
                  ✓ {rawInput.length} characters loaded. Click &quot;Parse with AI&quot; to continue.
                </div>
              )}
            </div>

            {/* Parse Button */}
            <div className="flex justify-end space-x-2">
              <Button
                onClick={parseWithAI}
                disabled={parsing || !rawInput.trim()}
                className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
                data-testid="parse-portfolio-btn"
              >
                {parsing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Parsing...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Parse with AI
                  </>
                )}
              </Button>
            </div>

            {/* Help text */}
            <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
              <p className="font-medium mb-1">Supported formats:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Copy/paste from Tastytrade, Schwab, TD Ameritrade, Fidelity, etc.</li>
                <li>CSV format with headers</li>
                <li>Plain text descriptions of your positions</li>
                <li>Screenshots text (if you type it out)</li>
              </ul>
            </div>
          </>
        ) : (
          <>
            {/* Parsed Positions Table */}
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="w-[100px]">Symbol</TableHead>
                    <TableHead className="w-[80px]">Type</TableHead>
                    <TableHead className="w-[80px]">Strike</TableHead>
                    <TableHead className="w-[120px]">Expiration</TableHead>
                    <TableHead className="w-[80px]">Qty</TableHead>
                    <TableHead className="w-[100px]">Entry $</TableHead>
                    <TableHead className="w-[100px]">Current $</TableHead>
                    <TableHead className="w-[80px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parsedPositions.map((pos, idx) => (
                    <TableRow key={idx} className={editingIndex === idx ? 'bg-blue-50' : ''}>
                      <TableCell>
                        <Input
                          value={pos.underlying_symbol || ''}
                          onChange={(e) => updatePosition(idx, 'underlying_symbol', e.target.value)}
                          className="h-8 w-full"
                          placeholder="AAPL"
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          value={pos.option_type || 'call'}
                          onValueChange={(v) => updatePosition(idx, 'option_type', v)}
                        >
                          <SelectTrigger className="h-8 w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="call">Call</SelectItem>
                            <SelectItem value="put">Put</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          value={pos.strike_price || ''}
                          onChange={(e) => updatePosition(idx, 'strike_price', parseFloat(e.target.value) || '')}
                          className="h-8 w-full"
                          placeholder="150"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="date"
                          value={pos.expiration_date || ''}
                          onChange={(e) => updatePosition(idx, 'expiration_date', e.target.value)}
                          className="h-8 w-full"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          value={pos.quantity || ''}
                          onChange={(e) => updatePosition(idx, 'quantity', parseInt(e.target.value) || '')}
                          className="h-8 w-full"
                          placeholder="1"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          value={pos.entry_premium || ''}
                          onChange={(e) => updatePosition(idx, 'entry_premium', parseFloat(e.target.value) || '')}
                          className="h-8 w-full"
                          placeholder="2.50"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          value={pos.current_premium || ''}
                          onChange={(e) => updatePosition(idx, 'current_premium', parseFloat(e.target.value) || '')}
                          className="h-8 w-full"
                          placeholder="3.00"
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deletePosition(idx)}
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between items-center pt-4">
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  onClick={addPosition}
                  className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Position
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowImportArea(true)}
                  className="text-gray-600"
                >
                  <ClipboardPaste className="h-4 w-4 mr-1" />
                  Import More
                </Button>
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  onClick={clearAll}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear All
                </Button>
                <Button
                  onClick={savePositions}
                  className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                  data-testid="save-imported-btn"
                >
                  <Save className="h-4 w-4 mr-1" />
                  Save Positions
                </Button>
              </div>
            </div>

            {/* Warning about AI parsing */}
            <div className="flex items-start space-x-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">Please review the parsed data</p>
                <p>AI parsing may occasionally misread values. Double-check strikes, quantities, and dates before saving.</p>
              </div>
            </div>
          </>
        )}

        {/* Saved Positions Section */}
        {savedPositions.length > 0 && (
          <div className="mt-6 pt-6 border-t">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center">
                <Briefcase className="h-5 w-5 mr-2 text-indigo-600" />
                Saved Positions ({filteredPositions.length}{filterPlatform !== 'all' ? ` of ${savedPositions.length}` : ''})
              </h3>
              <div className="flex items-center space-x-2">
                {/* Portfolio Health Analyzer Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowHealthAnalyzer(true)}
                  className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200 text-indigo-700 hover:from-indigo-100 hover:to-purple-100"
                >
                  <Activity className="h-4 w-4 mr-1" />
                  Health Check
                </Button>
                {/* Platform Filter */}
                <Select value={filterPlatform} onValueChange={setFilterPlatform}>
                  <SelectTrigger className="w-40 h-9">
                    <SelectValue placeholder="All Platforms" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Platforms</SelectItem>
                    {savedPlatforms.map(platformId => {
                      const platform = getPlatform(platformId);
                      return (
                        <SelectItem key={platformId} value={platformId}>
                          {platform.name}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSaved(!showSaved)}
                >
                  {showSaved ? 'Hide' : 'Show'}
                </Button>
              </div>
            </div>

            {showSaved && (
              <>
                {/* Selection Controls */}
                <div className="flex items-center justify-between mb-3 p-3 bg-gray-100 rounded-lg">
                  <div className="flex items-center space-x-3">
                    {/* Select All Checkbox */}
                    <div 
                      className="flex items-center space-x-2 cursor-pointer"
                      onClick={() => allSelected ? deselectAllPositions() : selectAllPositions()}
                    >
                      <Checkbox 
                        checked={allSelected}
                        onCheckedChange={() => allSelected ? deselectAllPositions() : selectAllPositions()}
                        data-testid="select-all-checkbox"
                      />
                      <span className="text-sm font-medium">
                        {allSelected ? 'Deselect All' : 'Select All'}
                      </span>
                    </div>
                    
                    {selectedForDelete.size > 0 && (
                      <Badge variant="secondary" className="bg-indigo-100 text-indigo-700">
                        {selectedForDelete.size} selected
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {/* Group Selected Button */}
                    {selectedForDelete.size >= 2 && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={openGroupModal}
                        className="bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100"
                        data-testid="group-selected-btn"
                      >
                        <Layers className="h-4 w-4 mr-1" />
                        Group ({selectedForDelete.size})
                      </Button>
                    )}
                    
                    {/* Delete Selected Button */}
                    {selectedForDelete.size > 0 && (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={deleteSelectedPositions}
                        className="bg-red-500 hover:bg-red-600"
                        data-testid="delete-selected-btn"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete Selected ({selectedForDelete.size})
                      </Button>
                    )}
                    
                    {/* Clear All Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearAllSaved}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Clear All
                    </Button>
                  </div>
                </div>

                {/* Position List */}
                <div className="space-y-2">
                  {filteredPositions.map((pos, idx) => {
                    const platform = getPlatform(pos.platform);
                    // Use the position's unique ID for all operations
                    const positionId = pos._id;
                    const isSelected = selectedForDelete.has(positionId);
                    const isEditing = editingPositionId === positionId;
                    const bullish = isBullish(pos);
                    const { pnl, pnlPercent } = calculatePnL(pos);
                    
                    // Determine border color based on bullish/bearish
                    const directionColor = bullish 
                      ? 'border-l-4 border-l-green-500' 
                      : 'border-l-4 border-l-red-500';
                    
                    // Editing mode - show expanded edit form
                    if (isEditing && editFormData) {
                      return (
                        <div 
                          key={`edit-${positionId}`}
                          className="p-4 rounded-lg border-2 border-indigo-400 bg-indigo-50 space-y-4"
                          data-testid={`edit-position-form-${idx}`}
                        >
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold text-indigo-700 flex items-center">
                              <Edit2 className="h-4 w-4 mr-2" />
                              Edit Position
                            </h4>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={cancelEditingSaved}
                              className="text-gray-500 hover:text-gray-700"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                          
                          {/* Edit Form Grid */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {/* Symbol */}
                            <div className="space-y-1">
                              <Label className="text-xs font-medium text-gray-600">Symbol</Label>
                              <Input
                                value={editFormData.underlying_symbol || ''}
                                onChange={(e) => updateEditFormField('underlying_symbol', e.target.value.toUpperCase())}
                                className="h-9"
                                placeholder="AAPL"
                                data-testid="edit-symbol-input"
                              />
                            </div>
                            
                            {/* Option Type */}
                            <div className="space-y-1">
                              <Label className="text-xs font-medium text-gray-600">Type</Label>
                              <Select
                                value={editFormData.option_type || 'call'}
                                onValueChange={(v) => updateEditFormField('option_type', v)}
                              >
                                <SelectTrigger className="h-9" data-testid="edit-type-select">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="call">Call</SelectItem>
                                  <SelectItem value="put">Put</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            
                            {/* Strike Price */}
                            <div className="space-y-1">
                              <Label className="text-xs font-medium text-gray-600">Strike</Label>
                              <Input
                                type="number"
                                value={editFormData.strike_price || ''}
                                onChange={(e) => updateEditFormField('strike_price', parseFloat(e.target.value) || '')}
                                className="h-9"
                                placeholder="150"
                                data-testid="edit-strike-input"
                              />
                            </div>
                            
                            {/* Expiration Date */}
                            <div className="space-y-1">
                              <Label className="text-xs font-medium text-gray-600">Expiration</Label>
                              <Input
                                type="date"
                                value={editFormData.expiration_date || ''}
                                onChange={(e) => updateEditFormField('expiration_date', e.target.value)}
                                className="h-9"
                                data-testid="edit-expiration-input"
                              />
                            </div>
                            
                            {/* Quantity */}
                            <div className="space-y-1">
                              <Label className="text-xs font-medium text-gray-600">Quantity</Label>
                              <Input
                                type="number"
                                value={editFormData.quantity || ''}
                                onChange={(e) => updateEditFormField('quantity', parseInt(e.target.value) || '')}
                                className="h-9"
                                placeholder="1"
                                data-testid="edit-quantity-input"
                              />
                            </div>
                            
                            {/* Entry Premium */}
                            <div className="space-y-1">
                              <Label className="text-xs font-medium text-gray-600">Entry $</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={editFormData.entry_premium || ''}
                                onChange={(e) => updateEditFormField('entry_premium', parseFloat(e.target.value) || '')}
                                className="h-9"
                                placeholder="2.50"
                                data-testid="edit-entry-input"
                              />
                            </div>
                            
                            {/* Current Premium */}
                            <div className="space-y-1">
                              <Label className="text-xs font-medium text-gray-600">Current $</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={editFormData.current_premium || ''}
                                onChange={(e) => updateEditFormField('current_premium', parseFloat(e.target.value) || '')}
                                className="h-9"
                                placeholder="3.00"
                                data-testid="edit-current-input"
                              />
                            </div>
                            
                            {/* Platform */}
                            <div className="space-y-1">
                              <Label className="text-xs font-medium text-gray-600">Platform</Label>
                              <Select
                                value={editFormData.platform || 'other'}
                                onValueChange={(v) => updateEditFormField('platform', v)}
                              >
                                <SelectTrigger className="h-9" data-testid="edit-platform-select">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {PLATFORMS.map(p => (
                                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          
                          {/* Save/Cancel Buttons */}
                          <div className="flex justify-end space-x-2 pt-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={cancelEditingSaved}
                              className="text-gray-600"
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={saveEditedPosition}
                              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                              data-testid="save-edit-btn"
                            >
                              <Save className="h-4 w-4 mr-1" />
                              Save Changes
                            </Button>
                          </div>
                        </div>
                      );
                    }
                    
                    // Normal display mode
                    const expirationStatus = getExpirationStatus(pos.expiration_date);
                    
                    return (
                      <div 
                        key={idx}
                        className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${directionColor} ${
                          isSelected 
                            ? 'bg-indigo-50 border-indigo-300' 
                            : expirationStatus.expiresSoon
                              ? 'bg-red-50 border-red-300 animate-pulse'
                              : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                        style={expirationStatus.expiresSoon ? {
                          animation: 'expiration-flash 1s ease-in-out infinite'
                        } : {}}
                      >
                        <style>{`
                          @keyframes expiration-flash {
                            0%, 100% { background-color: rgba(254, 202, 202, 0.5); border-color: rgb(252, 165, 165); }
                            50% { background-color: rgba(254, 226, 226, 1); border-color: rgb(248, 113, 113); }
                          }
                        `}</style>
                        <div className="flex items-center space-x-3 flex-wrap gap-y-1">
                          {/* Checkbox */}
                          <Checkbox 
                            checked={isSelected}
                            onCheckedChange={() => togglePositionSelection(positionId)}
                            data-testid={`position-checkbox-${idx}`}
                          />
                          
                          {/* Expiration Warning Badge */}
                          {expirationStatus.expiresSoon && (
                            <Badge className="text-xs bg-red-500 text-white animate-bounce font-bold">
                              ⚠️ {expirationStatus.status}
                            </Badge>
                          )}
                          
                          {/* Bullish/Bearish Indicator */}
                          <Badge className={`text-xs ${bullish ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {bullish ? '📈 BULL' : '📉 BEAR'}
                          </Badge>
                          
                          {/* Platform Badge */}
                          <Badge className={`text-xs ${platform.color}`}>
                            {platform.name}
                          </Badge>
                          
                          {/* Edited Badge */}
                          {(pos.edited_at || pos.needs_recalculation) && (
                            <Badge className="text-xs bg-yellow-100 text-yellow-700 border-yellow-300 animate-pulse">
                              ✏️ Edited - Recalculate
                            </Badge>
                          )}
                          
                          {/* Imported At Timestamp - Shows full date/time */}
                          {pos.imported_at && (
                            <div className="flex flex-col items-center justify-center min-w-[90px] px-2 py-1 bg-blue-50 rounded-lg border border-blue-200 mr-2">
                              <div className="flex items-center text-blue-600 mb-0.5">
                                <Clock className="h-3 w-3 mr-1" />
                                <span className="text-[10px] font-semibold">Imported</span>
                              </div>
                              <span className="text-[11px] font-medium text-blue-700 whitespace-nowrap">
                                {new Date(pos.imported_at).toLocaleDateString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric',
                                  year: '2-digit'
                                })}
                              </span>
                              <span className="text-[10px] text-blue-500 whitespace-nowrap">
                                {new Date(pos.imported_at).toLocaleTimeString('en-US', { 
                                  hour: 'numeric', 
                                  minute: '2-digit',
                                  hour12: true
                                })}
                              </span>
                            </div>
                          )}
                          
                          {/* Symbol */}
                          <div className={`font-semibold text-lg ${bullish ? 'text-green-700' : 'text-red-700'}`}>
                            {pos.underlying_symbol || pos.symbol}
                          </div>
                          
                          {/* Grouped Strategy Badge */}
                          {pos.is_spread && (
                            <Badge className="text-xs bg-indigo-100 text-indigo-700 border-indigo-300">
                              <Layers className="h-3 w-3 mr-1" />
                              {pos.strategy_name || pos.strategy_type}
                            </Badge>
                          )}
                          
                          {/* Option Type & Strike - Show differently for spreads */}
                          {pos.is_spread ? (
                            <div className="flex flex-wrap gap-1">
                              {pos.legs?.map((leg, legIdx) => (
                                <Badge 
                                  key={legIdx}
                                  variant="outline"
                                  className={`text-xs ${leg.option_type === 'call' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}
                                >
                                  {leg.quantity > 0 ? '+' : ''}{leg.quantity} {leg.option_type?.charAt(0).toUpperCase()}@${leg.strike_price}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <Badge variant={pos.option_type === 'call' ? 'default' : 'secondary'}>
                              {pos.option_type?.toUpperCase()} ${pos.strike_price}
                            </Badge>
                          )}
                          
                          <span className={`text-sm ${expirationStatus.expiresSoon ? 'text-red-600 font-bold' : 'text-gray-500'}`}>
                            Exp: {pos.expiration_date} {expirationStatus.expiresSoon && `(${expirationStatus.expiresInDays}d)`}
                          </span>
                          <span className="text-sm">
                            Qty: {pos.quantity > 0 ? '+' : ''}{pos.quantity}
                          </span>
                          
                          {/* Entry & Current Premium */}
                          {pos.entry_premium && (
                            <span className="text-sm text-gray-500">
                              Entry: ${pos.entry_premium}
                            </span>
                          )}
                          {pos.current_premium && (
                            <span className="text-sm text-gray-500">
                              Now: ${pos.current_premium}
                            </span>
                          )}
                          
                          {/* P/L Display */}
                          {pnlPercent !== null && (
                            <div className={`flex items-center space-x-1 px-2 py-1 rounded-md font-bold ${
                              pnlPercent >= 0 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-red-100 text-red-700'
                            }`}>
                              <span>{pnlPercent >= 0 ? '+' : ''}{pnlPercent.toFixed(1)}%</span>
                              {pnl !== null && (
                                <span className="text-xs">
                                  ({pnl >= 0 ? '+' : ''}${pnl.toFixed(0)})
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          {/* Edit Button */}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => startEditingSaved(positionId)}
                            className="text-blue-600 border-blue-200 hover:bg-blue-50"
                            data-testid={`edit-position-${pos.underlying_symbol}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          
                          {/* Recalculate Button - Always visible for AI recalculation */}
                          <Button
                            size="sm"
                            onClick={() => recalculatePosition(pos)}
                            disabled={recalculatingId === positionId}
                            className={`text-white ${
                              pos.edited_at || pos.needs_recalculation
                                ? 'bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 animate-pulse'
                                : 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700'
                            }`}
                            data-testid={`recalculate-${pos.underlying_symbol}`}
                          >
                            {recalculatingId === positionId ? (
                              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            ) : (
                              <Calculator className="h-4 w-4 mr-1" />
                            )}
                            {recalculatingId === positionId ? 'Calculating...' : 'Recalculate'}
                          </Button>
                          
                          <Button
                            size="sm"
                            onClick={() => handleAnalyze(pos)}
                            className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
                            data-testid={`analyze-imported-${pos.underlying_symbol}`}
                          >
                            <Brain className="h-4 w-4 mr-1" />
                            Analyze
                          </Button>
                          
                          {/* Manage Button - AI recommendations */}
                          {onManagePosition && (
                            <Button
                              size="sm"
                              onClick={() => onManagePosition(pos)}
                              className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white"
                              data-testid={`manage-imported-${pos.underlying_symbol}`}
                            >
                              <Settings2 className="h-4 w-4 mr-1" />
                              Manage
                            </Button>
                          )}
                          
                          {/* P&L Chart Button */}
                          <PayoffDiagramButton 
                            position={pos} 
                            currentPrice={pos.current_stock_price}
                            size="sm"
                          />
                          
                          {/* Ungroup Button - Only for grouped spreads */}
                          {pos.is_spread && pos.legs && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => ungroupSpread(positionId)}
                              className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                              title="Ungroup back into individual positions"
                            >
                              <Layers className="h-4 w-4" />
                            </Button>
                          )}
                          
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteSavedPosition(positionId)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
        
        {/* Recalculation Result Modal */}
        {showRecalcResult && recalculationResult && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <div className="p-6 border-b bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-t-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Calculator className="h-6 w-6" />
                    <div>
                      <h3 className="font-bold text-lg">AI Recalculation Results</h3>
                      <p className="text-sm opacity-90">
                        {recalculationResult.position.underlying_symbol} {recalculationResult.position.option_type?.toUpperCase()} ${recalculationResult.position.strike_price}
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={closeRecalcResult}
                    className="text-white hover:bg-white/20"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                {/* Current Price */}
                {recalculationResult.currentPrice && (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">Current Stock Price</span>
                    <span className="font-bold text-lg">${recalculationResult.currentPrice.toFixed(2)}</span>
                  </div>
                )}
                
                {/* AI Analysis */}
                {recalculationResult.analysis && (
                  <div className="space-y-3">
                    {/* Recommendation */}
                    {recalculationResult.analysis.recommendation && (
                      <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                        <h4 className="font-semibold text-indigo-700 mb-2">Recommendation</h4>
                        <p className="text-gray-700">{recalculationResult.analysis.recommendation}</p>
                      </div>
                    )}
                    
                    {/* Strategies */}
                    {recalculationResult.analysis.strategies && recalculationResult.analysis.strategies.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="font-semibold text-gray-700">Suggested Actions</h4>
                        {recalculationResult.analysis.strategies.map((strategy, idx) => (
                          <div key={idx} className="p-3 bg-gray-50 rounded-lg border">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium">{strategy.action || strategy.name}</span>
                              {strategy.confidence && (
                                <Badge className={`${
                                  strategy.confidence === 'high' ? 'bg-green-100 text-green-700' :
                                  strategy.confidence === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {strategy.confidence} confidence
                                </Badge>
                              )}
                            </div>
                            {strategy.explanation && (
                              <p className="text-sm text-gray-600">{strategy.explanation}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Raw Analysis Text */}
                    {recalculationResult.analysis.analysis && (
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-semibold text-gray-700 mb-2">Full Analysis</h4>
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">{recalculationResult.analysis.analysis}</p>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="text-xs text-gray-400 text-center pt-2">
                  Calculated at {new Date(recalculationResult.timestamp).toLocaleString()}
                </div>
              </div>
              
              <div className="p-4 border-t bg-gray-50 rounded-b-xl flex justify-end space-x-2">
                <Button variant="outline" onClick={closeRecalcResult}>
                  Close
                </Button>
                <Button 
                  onClick={() => {
                    closeRecalcResult();
                    handleAnalyze(recalculationResult.position);
                  }}
                  className="bg-gradient-to-r from-indigo-500 to-purple-600"
                >
                  <Brain className="h-4 w-4 mr-1" />
                  Full Analysis
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Group Positions Modal */}
        <Dialog open={showGroupModal} onOpenChange={setShowGroupModal}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Layers className="h-5 w-5 text-indigo-600" />
                Group Positions into Strategy
              </DialogTitle>
              <DialogDescription>
                Combine {selectedForGroup.size} selected positions into a multi-leg strategy.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Selected Positions Preview */}
              <div className="bg-muted/50 rounded-lg p-3">
                <h4 className="text-sm font-medium mb-2">Selected Positions:</h4>
                <div className="space-y-1">
                  {savedPositions
                    .filter(pos => selectedForGroup.has(pos._id))
                    .map((pos, idx) => (
                      <div key={pos._id} className="text-sm flex items-center justify-between">
                        <span>
                          <Badge variant="outline" className={pos.option_type === 'call' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}>
                            {pos.quantity > 0 ? '+' : ''}{pos.quantity} {pos.option_type?.charAt(0).toUpperCase()}
                          </Badge>
                          <span className="ml-2">${pos.strike_price}</span>
                          <span className="text-muted-foreground ml-2">
                            exp {pos.expiration_date?.substring(0, 10)}
                          </span>
                        </span>
                      </div>
                    ))}
                </div>
              </div>
              
              {/* Strategy Type Selection */}
              <div className="space-y-2">
                <Label>Strategy Type</Label>
                <Select value={groupStrategyType} onValueChange={setGroupStrategyType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select strategy type" />
                  </SelectTrigger>
                  <SelectContent>
                    {STRATEGY_TYPES.map(strategy => (
                      <SelectItem key={strategy.id} value={strategy.id}>
                        <div className="flex flex-col">
                          <span>{strategy.name}</span>
                          <span className="text-xs text-muted-foreground">{strategy.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Strategy Name */}
              <div className="space-y-2">
                <Label>Strategy Name</Label>
                <Input
                  value={groupStrategyName}
                  onChange={(e) => setGroupStrategyName(e.target.value)}
                  placeholder="e.g., TDC Long Straddle"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowGroupModal(false)}>
                Cancel
              </Button>
              <Button 
                onClick={groupPositions}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <Link2 className="h-4 w-4 mr-1" />
                Group Positions
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Portfolio Health Analyzer Modal */}
        <Dialog open={showHealthAnalyzer} onOpenChange={setShowHealthAnalyzer}>
          <DialogContent className="bg-slate-900 border-slate-700 max-w-6xl max-h-[90vh] overflow-y-auto">
            <PortfolioHealthAnalyzer 
              positions={savedPositions}
              onClose={() => setShowHealthAnalyzer(false)}
            />
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default PortfolioImport;
